// SDMS batch Project Notes — ALPACA-parity grid popup wired to the real backend.
//
// Restores the full grid UI (Title · BusinessObject · SourceID · Note · Author ·
// Date, with New / Delete / Search / sort / per-column filter + a rich-text
// editor) from the original SDMS design, but wired to AnalysisController's
// /result-validation/{batchOid}/project-notes routes so add / edit / delete
// really persist to dbo.Notes (the earlier grid version was in-memory only).
//
// Author-only edit + delete (CLAUDE.md §14a Rule C): the server returns HTTP 403
// with the canonical message and each row carries a server-computed `canEdit`, so
// the UI mirrors (never trusts) the gate.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { api } from '../../api/client';
import { useAuthStore } from '../../auth/store';
import { fmtLocalDateTime, fmtInZone, getClientTimeZone } from '../../utils/datetime';
import { ColFilterButton, passesColumnFilter, cascadeRows, type ColumnFilterValue } from '../../components/ColumnFilterDropdown';
import { useSort, SortableHeader } from '../../components/SortableHeader';
import { warn, errorDlg, useDeleteFlow } from '../../components/confirm';
import './SdmsProjectNotes.css';
// The ALPACA Note body is RICH TEXT (DevExpress RichEdit), so reuse the shared
// rich-text editor (execCommand-based, no extra dependency) + its styles — the
// same component the Employee Resume tab uses — instead of a bespoke toolbar
// (CLAUDE.md rule 8 "don't reinvent").  The note body is stored/loaded as HTML.
import { RichTextEditor } from '../settings/systemManagement/RichTextEditor';
import '../accounting/accounting.css';

// ── server shape (dbo.Notes row, camelCase JSON) ─────────────────────────
interface NoteRow {
  oid: string;
  title: string | null;
  plainText: string | null;
  noteSource: string | null;   // → BusinessObject column
  sourceId: string | null;     // → SourceID column
  createdBy: string | null;    // → Author column (Employee DisplayName / E-code)
  createdDate: string | null;  // → Date column
  canEdit: boolean;            // server-computed author-only gate
}

// ── UI row (flattened for the grid) ──────────────────────────────────────
interface GridNote {
  id:             string;
  title:          string;
  businessObject: string;
  sourceId:       string;
  note:           string;
  author:         string;
  date:           string;      // ISO
  canEdit:        boolean;
}

/** Plain-text preview of a note body.  The body is HTML (rich-text editor), so the
 *  grid cell + the column filter show the STRIPPED text — never raw tags — while the
 *  editor keeps the full HTML. */
function stripHtml(html: string): string {
  if (!html) return '';
  if (!/[<&]/.test(html)) return html;              // already plain
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent ?? '').replace(/ /g, ' ');
}

const toGrid = (r: NoteRow): GridNote => ({
  id:             r.oid,
  title:          r.title ?? '',
  businessObject: r.noteSource ?? '',
  sourceId:       r.sourceId ?? '',
  note:           r.plainText ?? '',
  author:         r.createdBy ?? '',
  date:           r.createdDate ?? '',
  canEdit:        !!r.canEdit,
});

// ── shared bump so an add/edit/delete refreshes any open badge ───────────
const useNotesBump = create<{ v: number; bump: () => void }>((set) => ({
  v: 0,
  bump: () => set(s => ({ v: s.v + 1 })),
}));

/** Refresh the Project Notes badge + any OPEN popup from outside the popup. The
 *  SDMS Validate / Submit flows persist a Check Point comment whose backend sync
 *  SP writes 'Data Package Check Point' notes server-side — nothing in the popup
 *  fires then, so the badge/popup stayed stale until a page remount (navigate away
 *  and back). Callers invoke this after that save so both refetch immediately. */
export const refreshProjectNotes = (): void => useNotesBump.getState().bump();

const notesBase = (batchOid: string) =>
  `/api/v1/analysis/result-validation/${batchOid}/project-notes`;

/** Append ?jobOid= when the caller is the MANUAL Result Validation detail — that
 *  scopes the notes to the one displayed job (ALPACA "Result Validation"), instead
 *  of the whole QC batch (SDMS). SDMS callers pass no jobOid → batch scope. */
const withJob = (url: string, jobOid?: string | null) =>
  jobOid ? `${url}${url.includes('?') ? '&' : '?'}jobOid=${jobOid}` : url;

/** Count of notes for a batch (or one job when jobOid is given), for the toolbar
 *  "Project Notes (n)" badge. Re-fetches whenever the shared bump changes. */
export function useNotesCount(batchOid: string, jobOid?: string | null, baseUrl?: string, nonce?: number): number {
  const [count, setCount] = useState(0);
  const v = useNotesBump(s => s.v);
  useEffect(() => {
    if (!batchOid && !baseUrl) { setCount(0); return; }
    let alive = true;
    // baseUrl callers (e.g. Field Data Entry) own the whole job-scoped route and
    // must NOT get ?jobOid= appended — the job is already in their path.
    const url = baseUrl ?? withJob(notesBase(batchOid), jobOid);
    api.get(url, { silentLoading: true } as never)
      .then(r => { if (alive) setCount(((r.data?.data ?? []) as NoteRow[]).length); })
      .catch(() => { if (alive) setCount(0); });
    return () => { alive = false; };
    // `nonce` lets a caller force THIS badge to refetch via React's own render
    // cycle — independent of the shared-store bump `v` — after it writes notes
    // server-side (SDMS Submit/Validate). (2026-10-08)
  }, [batchOid, jobOid, baseUrl, v, nonce]);
  return count;
}

// ── current-user display name (matches the Author column / server stamp) ──
function useCurrentAuthorName(): string {
  const user = useAuthStore((s) => s.user);
  return user?.name || user?.email || user?.id || '';
}

// ── list popup props (unchanged contract — SdmsPackageDetail depends on it) ─
export interface NotesListPopupProps {
  /** The batch Oid (route key for the API). */
  batchId: string;
  batchTest: string;
  /** AnalyticalBatchID (e.g. "QB26060807_014") — title bar + editor SourceID. */
  batchLabel: string;
  /** The creating form's caption — a NEW note's "Business Object" and its saved
   *  NoteSource/NameSource (ALPACA View.Caption): "Result Approval" / "Result
   *  Validation" for the manual Data-Review forms.  SDMS callers omit it. */
  businessObject?: string;
  /** MANUAL Result Validation only: the displayed job's Oid. When set, notes are
   *  scoped to that ONE job (ALPACA "Result Validation" — representative job in
   *  QCBatchID mode, selected job in JobID mode), not the whole QC batch. SDMS
   *  callers omit it → batch scope. */
  jobOid?: string | null;
  onClose: () => void;
  /** Fired after a note add/edit/delete so callers can refresh a badge. */
  onChanged?: () => void;
  /** When true the popup is VIEW-ONLY — the New/Delete actions are hidden and the
   *  editor opens read-only (existing notes can still be read in full). Callers pass
   *  this to gate note authoring by their own permission (e.g. Result Entry Write).
   *  Defaults to false so existing callers keep full add/edit/delete. Author-only
   *  edit/delete (§14a Rule C) still applies on top when this is false. */
  readOnly?: boolean;
  /** Callers that own their OWN job-scoped notes route (e.g. Field Data Entry's
   *  /api/v1/field-data-entry/{jobOid}/project-notes) pass it here. When set it
   *  REPLACES the default Analysis route and no ?jobOid= is appended — the job is
   *  already in the path. Row URLs become `${baseUrl}/${noteOid}`. Omit it and the
   *  existing batch / ?jobOid= behaviour is unchanged. */
  baseUrl?: string;
}

const errText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

const AUTHOR_ONLY_MSG = 'You are not authorized to modify a note created by another author.';

/** Project Notes — ALPACA-parity grid + author-only edit/delete. Batch-scoped for
 *  SDMS; job-scoped when jobOid is supplied (manual Result Validation). */
export function NotesListPopup({ batchId, batchTest, batchLabel, businessObject = 'SDMS Result Validation', jobOid, onClose, onChanged, readOnly = false, baseUrl }: NotesListPopupProps) {
  const bump = useNotesBump(s => s.bump);
  // Reload the list whenever ANYONE bumps — an in-popup add/edit/delete OR an
  // external note write (SDMS Validate/Submit → refreshProjectNotes). (2026-10-08)
  const notesV = useNotesBump(s => s.v);
  // baseUrl (Field Data Entry) owns its route outright; otherwise all note calls
  // carry ?jobOid= when present (job scope), else batch scope.
  const listUrl = baseUrl ?? withJob(notesBase(batchId), jobOid);
  const rowUrl = (id: string) =>
    baseUrl ? `${baseUrl}/${id}` : withJob(`${notesBase(batchId)}/${id}`, jobOid);

  const [rows,    setRows]    = useState<GridNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editorOpen, setEditorOpen] = useState<GridNote | 'new' | null>(null);
  const [colFilters, setColFilters] = useState<Record<string, ColumnFilterValue>>({});
  const runDelete = useDeleteFlow();   // shared danger-confirm → delete → "Deleted." (CLAUDE.md rule 7)
  const sorter = useSort<GridNote>(null, null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(listUrl, { silentLoading: true } as never);
      setRows(((r.data?.data ?? []) as NoteRow[]).map(toGrid));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  // Load on mount AND on every bump (notesV) — so an external note write
  // (SDMS Validate/Submit) refreshes an open popup, not just the badge.
  useEffect(() => { void load(); }, [load, notesV]);

  const afterChange = useCallback(async () => {
    // bump() → notesV changes → the load effect refetches (ONE load path, which
    // also refreshes the parent badge + any other open popup). onChanged keeps the
    // caller's own badge in sync immediately.
    bump();
    onChanged?.();
  }, [bump, onChanged]);

  // Propagate count to the parent toolbar badge on every change.
  useEffect(() => { onChanged?.(); }, [rows, onChanged]);

  // Esc closes the list (unless a child popup is open).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !editorOpen) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, editorOpen]);

  // Formatters per column — used by BOTH the cell render AND the filter list.
  const FORMATTERS = useMemo<Record<string, (n: GridNote) => string>>(() => ({
    title:          (n) => n.title,
    businessObject: (n) => n.businessObject,
    sourceId:       (n) => n.sourceId,
    note:           (n) => stripHtml(n.note),
    author:         (n) => n.author,
    date:           (n) => (n.date ? (fmtLocalDateTime(n.date) || '') : ''),
  }), []);

  // filtering: global search + per-column filters (AND).
  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return rows.filter(n => {
      if (t) {
        const hay = [n.title, n.businessObject, n.sourceId, n.note, n.author].join(' ').toLowerCase();
        if (!hay.includes(t)) return false;
      }
      for (const k of Object.keys(colFilters)) {
        const sel = colFilters[k];
        if (!sel) continue;
        if (!passesColumnFilter(sel, FORMATTERS[k]?.(n) ?? '')) return false;
      }
      return true;
    });
  }, [rows, search, colFilters, FORMATTERS]);

  const dropdownRowsFor = (key: string) =>
    cascadeRows(rows, colFilters, key, (n, k) => FORMATTERS[k]?.(n) ?? '');

  const sorted = useMemo(
    () => sorter.applyByFormatted(filtered, (n, k) => FORMATTERS[k]?.(n) ?? ''),
    [sorter, filtered, FORMATTERS],
  );

  const toggleSel = (id: string, on: boolean) => {
    const s = new Set(selected);
    if (on) s.add(id); else s.delete(id);
    setSelected(s);
  };
  const toggleAll = (on: boolean) => {
    setSelected(on ? new Set(sorted.map(n => n.id)) : new Set());
  };

  const doDelete = () => {
    if (readOnly) return;   // note actions gated by the caller's write permission
    const targets = rows.filter(n => selected.has(n.id));
    if (!targets.length) return;
    // Author-only (§14a Rule C): block early if any selected note isn't the
    // caller's — the server enforces this too (403), this just avoids the round-trip.
    if (targets.some(n => !n.canEdit)) { void warn(AUTHOR_ONLY_MSG); return; }
    const ids = targets.map(t => t.id);
    const n = ids.length;
    // Shared delete flow (CLAUDE.md rule 7 / §18.6): danger confirm → blocking delete
    // → "Deleted." acknowledgement — NEVER a bespoke confirm dialog or silent delete.
    void runDelete({
      message: `Delete ${n} note${n > 1 ? 's' : ''}?`,
      delete: async () => { for (const id of ids) await api.delete(rowUrl(id)); },
      onDeleted: () => { setSelected(new Set()); void afterChange(); },
      onError: (e) => { void errorDlg(errText(e, 'Could not delete the note(s).')); void load(); },
    });
  };

  const saveNote = async (data: { title: string; note: string }, existingId?: string) => {
    if (readOnly) return;   // note actions gated by the caller's write permission
    try {
      if (existingId) {
        await api.put(rowUrl(existingId), { title: data.title || null, text: data.note });
      } else {
        // `source` = the creating form's caption → the note's NoteSource/NameSource
        // (ALPACA View.Caption), so a note added here is tagged "Result Approval" etc.
        await api.post(listUrl, { title: data.title || null, text: data.note, source: businessObject });
      }
      setEditorOpen(null);
      await afterChange();
    } catch (e) {
      // Surface author-only / validation errors from the server through the shared
      // blocking dialog (§18.6) — the client author-gate usually catches this first.
      void errorDlg(errText(e, existingId ? 'Could not update the note.' : 'Could not add the note.'));
    }
  };

  const headAll = sorted.length > 0 && sorted.every(n => selected.has(n.id));
  const headSome = !headAll && sorted.some(n => selected.has(n.id));
  const headRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (headRef.current) headRef.current.indeterminate = headSome; }, [headSome]);

  return createPortal(
    <div className="np-ov" onClick={onClose}>
      <div className="np-win" onClick={e => e.stopPropagation()}>
        <div className="np-title">
          <i className="ti ti-notes" />
          Project Notes - {batchLabel}
          <button className="np-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="np-tb">
          {!readOnly && (
            <button className="np-btn" onClick={() => setEditorOpen('new')}>
              <i className="ti ti-plus" /> New
            </button>
          )}
          {!readOnly && (
            <button className="np-btn" onClick={doDelete} disabled={selected.size === 0}>
              <i className="ti ti-trash" /> Delete
            </button>
          )}
          <span className="np-search">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
            <span className="np-mg"><i className="ti ti-search" /></span>
          </span>
        </div>
        <div className="np-body">
          <div className="np-scroll">
            <table className="np-table">
              <colgroup>
                <col style={{ width: 34 }} />
                <col style={{ width: 42 }} />
                <col style={{ width: 158 }} />
                <col style={{ width: 168 }} />
                <col style={{ width: 150 }} />
                <col />
                <col style={{ width: 106 }} />
                <col style={{ width: 138 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="np-chk">
                    <input ref={headRef} type="checkbox" checked={headAll}
                      onChange={e => toggleAll(e.target.checked)} />
                  </th>
                  <th />
                  {(['title','businessObject','sourceId','note','author','date'] as const).map(k => {
                    const label = ({ title:'Title', businessObject:'BusinessObject', sourceId:'SourceID',
                                     note:'Note', author:'Author', date:'Date' } as const)[k];
                    // Title / BusinessObject / SourceID are sortable — matches ALPACA
                    // where Note / Author / Date show only a filter chevron.
                    const sortable = k === 'title' || k === 'businessObject' || k === 'sourceId';
                    return (
                      <th key={k}
                          onClick={sortable ? () => sorter.setSort(k) : undefined}
                          style={sortable ? { cursor: 'pointer' } : undefined}>
                        <span className="np-h">
                          {sortable
                            ? <SortableHeader label={label} sortKey={k} sort={sorter.sort} />
                            : <span className="np-h-label">{label}</span>}
                          <span onClick={(e) => e.stopPropagation()}>
                            <ColFilterButton
                              variant="inline"
                              col={k}
                              label={label}
                              rows={dropdownRowsFor(k)}
                              formatter={(n: GridNote) => FORMATTERS[k]!(n)}
                              value={colFilters[k] ?? null}
                              onChange={(v) => setColFilters(prev => ({ ...prev, [k]: v }))}
                              // Order the value list the SAME way the grid is sorted
                              // when this column is the active sort (else preserve
                              // display order) — filter never disagrees with the
                              // visible rows (user 2026-07-08).
                              sortDir={sorter.sort.key === k ? sorter.sort.dir : null}
                            />
                          </span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map(n => (
                  <tr key={n.id}>
                    <td className="np-chk">
                      <input type="checkbox" checked={selected.has(n.id)}
                        onChange={e => toggleSel(n.id, e.target.checked)} />
                    </td>
                    <td className="np-action">
                      {/* ALPACA parity: the edit icon is ALWAYS enabled — clicking a
                          note authored by someone else shows the author-only validation
                          (LIMS ProjectNotesViewController) instead of opening the editor.
                          The API 403 stays the authoritative gate (§14a Rule C). */}
                      <button className="np-edit"
                        title={readOnly ? 'View' : 'Edit'}
                        onClick={() => { if (readOnly || n.canEdit) setEditorOpen(n); else void warn(AUTHOR_ONLY_MSG); }}>
                        <i className={`ti ${readOnly ? 'ti-eye' : 'ti-edit'}`} />
                      </button>
                    </td>
                    <td>{n.title}</td>
                    <td>{n.businessObject}</td>
                    <td>{n.sourceId}</td>
                    <td><div className="np-note">{n.note}</div></td>
                    <td>{n.author}</td>
                    <td>{n.date ? (fmtLocalDateTime(n.date) || '') : ''}</td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={8} className="np-empty">
                    {/* "record", not "batch": this popup is shared by every form that
                        owns a notes route (SDMS batch, job, document, …), so the empty
                        state must not name one caller's noun. */}
                    {loading ? 'Loading…' : 'No notes for this record.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="np-foot">
          <span>Page 1 of 1 ({sorted.length} item{sorted.length === 1 ? '' : 's'})</span>
          <span className="np-psz">Page size
            <select defaultValue={20}><option>20</option><option>50</option><option>100</option></select>
          </span>
        </div>
      </div>

      {/* Editor — null = closed, 'new' = create, GridNote = edit */}
      {editorOpen && (
        <NoteEditorPopup
          note={editorOpen === 'new' ? null : editorOpen}
          batchTest={batchTest}
          batchLabel={batchLabel}
          businessObject={businessObject}
          forceReadonly={readOnly}
          onClose={() => setEditorOpen(null)}
          onSave={(data, id) => void saveNote(data, id)}
        />
      )}
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
//   Editor popup
// ─────────────────────────────────────────────────────────────────────────

interface NoteEditorPopupProps {
  /** Existing note to edit, or null when creating. */
  note:       GridNote | null;
  batchTest:  string;
  batchLabel: string;
  /** Creating form's caption — the "Business Object" (+ "Name Source") for a NEW note. */
  businessObject: string;
  /** When true the editor is forced VIEW-ONLY regardless of authorship — the caller
   *  lacks write permission (e.g. Result Entry read-only user). */
  forceReadonly?: boolean;
  onClose:    () => void;
  onSave:     (data: { title: string; note: string }, existingId?: string) => void;
}

function NoteEditorPopup({ note, batchTest, batchLabel, businessObject: sourceCaption, forceReadonly = false, onClose, onSave }: NoteEditorPopupProps) {
  const me = useCurrentAuthorName();
  const isNew = note === null;
  // View-only when the caller has no write permission, OR (author-only, §14a Rule C)
  // when editing a note the server did not mark canEdit.
  const readonly = forceReadonly || (!isNew && !note!.canEdit);

  const [title, setTitle] = useState(isNew ? '' : note!.title);
  // Note body is HTML (shared RichTextEditor).  New = empty; edit = the note's HTML.
  const [html, setHtml] = useState(isNew ? '' : note!.note);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    const t = title.trim();
    const plain = stripHtml(html).trim();   // visible text drives the required-Note check
    // LIMS Notes BO validation: BOTH Title and the Note body are required
    // ([RuleRequiredField] on Title + Text).  Surface every failing rule through the
    // SHARED blocking dialog (BO UI Standard §13 / dialogBus `warn` — a validation
    // summary with a bulleted list), matching ALPACA's "Problems were detected during
    // data validation" box.  NEVER a bespoke inline banner (CLAUDE.md rule 7 / §18.6).
    const problems: string[] = [];
    if (!plain) problems.push('"Note" must not be empty.');
    if (!t)     problems.push('"Title" must not be empty.');
    if (problems.length) {
      void warn('Problems were detected during data validation.', { list: problems });
      return;
    }
    onSave({ title: t, note: html }, isNew ? undefined : note!.id);
  };

  const authorDisplay = isNew ? (me || '(current user)') : note!.author;
  // Business Object = the creating form's caption for a NEW note, or the note's own
  // source when editing (ALPACA sets NoteSource = NameSource = View.Caption, e.g.
  // "Result Approval").  Name Source is fed via batchTest by the caller.
  const businessObj = isNew ? sourceCaption : (note!.businessObject || sourceCaption);
  const dateDisplay = isNew
    // NEW note: show the SERVER/lab (Central) current time — matches what the
    // backend stamps on save + GoLive's server clock (not the browser's local
    // time). fmtInZone renders "now" in the client/lab timezone (2026-08-31).
    ? (fmtInZone(new Date(), getClientTimeZone()) || '')
    : (note!.date ? (fmtLocalDateTime(note!.date) || '') : '');

  return (
    <div className="ne-ov" onClick={onClose}>
      <div className="ne-win" onClick={e => e.stopPropagation()}>
        <div className="ne-title">
          Note
          <button className="ne-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="ne-body">
          {readonly && (
            <div className="ne-ro">
              <b>Read-only.</b> Authored by <b>{note!.author}</b> — only the author may edit or delete this note.
            </div>
          )}
          <div className="ne-grid">
            <label className="ne-req">Title:</label>
            <input value={title} onChange={e => setTitle(e.target.value)} disabled={readonly} />
            <label>Business Object:</label>
            <input value={businessObj} disabled />
            <label>Source ID:</label>
            <input value={batchLabel} disabled />
            <label>Author:</label>
            <input value={authorDisplay} disabled />
            <label>Name Source:</label>
            <input value={batchTest} disabled />
            <label>Date:</label>
            <input value={dateDisplay} disabled />
          </div>
          <div className="ne-ed">
            <div className="ne-ed-tabs"><div className="ne-t">Note</div></div>
            {/* Rich-text body (shared RichTextEditor: font · size · colour · B/I/U/strike
                · align · lists · indent · paragraph styles · link · clear).  A read-only
                note renders its HTML as a static preview. */}
            {readonly
              ? <div className="ne-area ne-ro-view" dangerouslySetInnerHTML={{ __html: html }} />
              : <RichTextEditor value={html} onChange={setHtml} />}
          </div>
        </div>
        <div className="ne-foot">
          {!readonly && <button className="ne-ok" onClick={submit}>OK</button>}
          <button className="ne-cancel" onClick={onClose}>{readonly ? 'Close' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}
